import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getDeliveryDashboard,
  getDeliveryJobs,
  getDeliveryJob,
  advanceDeliveryJob,
  assignDeliveryJob,
  deliverJob,
  completeJob,
  cancelJob,
  getDeliveryRoutes,
  saveDeliveryRoute,
  deleteDeliveryRoute,
  getDeliveryUsers,
  assignRouteUser,
  getActiveDeliveryRouteSession,
  arriveDeliveryRoutePointApi,
  completeDeliveryRoutePointApi,
  completeDeliveryRouteSessionApi,
  updateDeliveryLiveLocation,
  saveDeliveryRouteAssignmentApi,
  getDeliveryRoutesAssignmentsApi,
  deleteDeliveryRouteAssignmentApi,
  deleteDeliveryRouteArea,
  saveDeliveryRouteArea,
  getDeliveryRouteAreas,
  getDeliveryRoutePointsApi,
  saveDeliveryRoutePointApi,
  deleteDeliveryRoutePointApi,
  deleteDeliveryRouteApi,
  saveDeliveryRouteApi,
  getDeliveryRoutesApi,
  getMyDeliveryRoute,
  startDeliveryRouteSessionApi,
} from "../../services/delivery";


// ============================
// Dashboard
// ============================

export const fetchDeliveryDashboard = createAsyncThunk(
  "delivery/fetchDashboard",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await getDeliveryDashboard(payload);
      return res.data?.data || res.data || null;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// ============================
// Jobs
// ============================

export const fetchDeliveryJobs = createAsyncThunk(
  "delivery/fetchJobs",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await getDeliveryJobs(payload);
      const raw = res.data?.data || res.data || [];
      // API returns {jobs: [...], total, page, limit} — extract array
      const jobs = Array.isArray(raw) ? raw : (raw.jobs || []);
      return jobs.map((j) => ({
        ...j,
        voucher_no: j.sale_voucher_no || j.voucher_no,
      }));
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchDeliveryJob = createAsyncThunk(
  "delivery/fetchJob",
  async (id, { rejectWithValue }) => {
    try {
      const res = await getDeliveryJob(id);
      const raw = res.data?.data || res.data || null;
      if (!raw) return null;
      // Flatten: merge job fields with items/history at the top level
      const { job = {}, items = [], history = [] } = raw;
      return {
        ...job,
        voucher_no: job.sale_voucher_no,
        items,
        status_history: history,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const advanceJob = createAsyncThunk(
  "delivery/advanceJob",
  async ({ id, notes }, { rejectWithValue, dispatch }) => {
    try {
      const res = await advanceDeliveryJob(id, { notes });
      dispatch(fetchDeliveryJob(id));
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const assignJob = createAsyncThunk(
  "delivery/assignJob",
  async ({ id, step, user_id }, { rejectWithValue, dispatch }) => {
    try {
      const res = await assignDeliveryJob(id, { step, user_id });
      dispatch(fetchDeliveryJob(id));
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const markDelivered = createAsyncThunk(
  "delivery/markDelivered",
  async ({ id, notes }, { rejectWithValue, dispatch }) => {
    try {
      const res = await deliverJob(id, { notes });
      dispatch(fetchDeliveryJob(id));
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const markComplete = createAsyncThunk(
  "delivery/markComplete",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const res = await completeJob(id);
      dispatch(fetchDeliveryJob(id));
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const cancelDeliveryJob = createAsyncThunk(
  "delivery/cancelJob",
  async ({ id, notes }, { rejectWithValue, dispatch }) => {
    try {
      const res = await cancelJob(id, { notes });
      dispatch(fetchDeliveryJob(id));
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// ============================
// Routes
// ============================


export const fetchDeliveryRoutes1 = createAsyncThunk(
  "delivery/fetchRoutes",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await getDeliveryRoutes(payload);
      return res.data?.data || res.data || [];
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const saveDeliveryRouteThunk1 = createAsyncThunk(
  "delivery/saveRoute",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const res = await saveDeliveryRoute(payload);
      dispatch(fetchDeliveryRoutes({}));
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteDeliveryRouteThunk1 = createAsyncThunk(
  "delivery/deleteRoute",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const res = await deleteDeliveryRoute(id);
      dispatch(fetchDeliveryRoutes({}));
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchDeliveryRoutes = createAsyncThunk(
  "workRoutes/fetchWorkRoutes",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getDeliveryRoutesApi();

      return res.data.data || [];
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const saveDeliveryRouteThunk = createAsyncThunk(
  "workRoutes/saveWorkRoute",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const res = await saveDeliveryRouteApi(payload);

      dispatch(fetchDeliveryRoutes());

      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const deleteDeliveryRouteThunk = createAsyncThunk(
  "workRoutes/deleteWorkRoute",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const res = await deleteDeliveryRouteApi(payload);

      dispatch(fetchDeliveryRoutes());

      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const fetchDeliveryUsers = createAsyncThunk(
  "delivery/fetchUsers",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getDeliveryUsers();
      return res.data?.data || res.data || [];
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const assignDeliveryRouteUser = createAsyncThunk(
  "delivery/assignRouteUser",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const res = await assignRouteUser(payload);
      dispatch(fetchDeliveryRoutes({}));
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchDeliveryRoutePoints = createAsyncThunk(
  "delivery/fetchRoutePoints",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getDeliveryRoutePointsApi();

      return res.data.data || [];
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const saveRoutePoint = createAsyncThunk(
  "delivery/saveRoutePoint",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const res = await saveDeliveryRoutePointApi(payload);

      dispatch(fetchDeliveryRoutePoints());

      return res.data;
    } catch (err) {
      console.error("saveRoutePoint API error:", err);

      return rejectWithValue({
        message:
          err.response?.data?.message ||
          err.message ||
          "Failed to save route point.",
      });
    }
  },
);

export const deleteDeliveryRoutePoint = createAsyncThunk(
  "delivery/deleteRoutePoint",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const res = await deleteDeliveryRoutePointApi(payload);

      dispatch(fetchDeliveryRoutePoints());

      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const fetchDeliveryAssignments = createAsyncThunk(
  "delivery/fetchRouteAssignments",

  async (_, { rejectWithValue }) => {
    try {
      const res = await getDeliveryRoutesAssignmentsApi();

      return res.data.data || [];
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);


export const saveDeliveryAssignment = createAsyncThunk(
  "delivery/saveRouteAssignment",

  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const res = await saveDeliveryRouteAssignmentApi(payload);

      dispatch(fetchDeliveryAssignments());

      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const deleteDeliveryAssignment = createAsyncThunk(
  "delivery/deleteRouteAssignment",

  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const res = await deleteDeliveryRouteAssignmentApi(payload);

      dispatch(fetchDeliveryAssignments());

      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const fetchDeliveryAreas = createAsyncThunk(
  "delivery/fetchDeliveryAreas",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getDeliveryRouteAreas();

      return res.data.data || [];
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const saveDeliveryArea = createAsyncThunk(
  "delivery/saveDeliveryArea",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const res = await saveDeliveryRouteArea(payload);

      dispatch(fetchDeliveryAreas());

      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const deleteDeliveryArea = createAsyncThunk(
  "delivery/deleteDeliveryArea",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const res = await deleteDeliveryRouteArea(payload);

      dispatch(fetchDeliveryAreas());

      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);


// ============================
// Delivery Execution
// ============================

export const fetchActiveDeliverySession = createAsyncThunk(
  "delivery/fetchActiveDeliverySession",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await getActiveDeliveryRouteSession(payload);
      return res.data?.data || res.data || null;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchMyDeliveryRoute = createAsyncThunk(
  "delivery/fetchMyDeliveryRoute",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getMyDeliveryRoute();

      return res.data?.data || null;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message
      );
    }
  }
);

export const arriveDeliveryRoutePoint = createAsyncThunk(
  "delivery/arriveRoutePoint",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const res = await arriveDeliveryRoutePointApi(payload);

      dispatch(fetchActiveDeliverySession());

      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const completeDeliveryRoutePoint = createAsyncThunk(
  "delivery/completeRoutePoint",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const res = await completeDeliveryRoutePointApi(payload);
       dispatch(fetchActiveDeliverySession());
      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const completeDeliveryRouteSession = createAsyncThunk(
  "delivery/completeRouteSession",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const res = await completeDeliveryRouteSessionApi(payload);

      dispatch(fetchActiveDeliverySession());

      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateDriverLocation = createAsyncThunk(
  "delivery/updateDriverLocation",
  async (payload, { rejectWithValue }) => {
    try {
      await updateDeliveryLiveLocation(payload);

      return {
        lat: payload.latitude,
        lng: payload.longitude,
        accuracy: payload.accuracy,
        updated_at: new Date().toISOString(),
      };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const startDeliveryRouteSession = createAsyncThunk(
  "delivery/startDeliveryRouteSession",
  async ({ assignment_id }, { rejectWithValue, dispatch }) => {
    try {
      const res = await startDeliveryRouteSessionApi({
        assignment_id,
      });

      await dispatch(fetchActiveDeliverySession());

      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message
      );
    }
  }
);